/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { OfferRejectionReason } from 'types/entities/specific/OfferRejectionReason'
import { XlsxImportResult } from 'types/import/XlsxImportResult'

export type StoredRejectionReason = OfferRejectionReason

export type MergeAuditApiChange = {
    id: number
    created_at: string
    related_model_type: string
    related_model_id: number
    attribute: string
    action: string
    replaced: {
        from: string | null
        to: string | null
    }
    metadata: Record<string, unknown> | null
}

export type MergeAuditApiMerge = {
    main_model_id: number
    main_model_name: string | null
    merged_model_id: number
    merged_model_name: string | null
    related_model_ids: Record<string, number[]>
    changes: MergeAuditApiChange[]
}

export type MergeAuditApiResponse = {
    company_id: number | null
    merges: MergeAuditApiMerge[]
}

export type MergeAuditLogEntry = {
    id: string
    createdAt: string
    mainCompanyId: number
    mainCompanyName?: string
    mergedCompanyIds: number[]
    mergedCompanyNames?: string[]
    mainModelId?: number
    mergedModelId?: number
    relatedModelId?: number
    relatedModelType?: string
    action?: string
    attribute?: string
    oldValue?: string | null
    newValue?: string | null
}

export type ImportBacklogEntry = {
    id: string
    entity: 'companies' | 'contacts'
    fileName: string
    createdAt: string
    result: XlsxImportResult
}

export interface CrmState {
    rejectionReasons: StoredRejectionReason[] | null
    mergeAuditLog: MergeAuditLogEntry[]
    importBacklog: ImportBacklogEntry[]
}

const MAX_MERGE_AUDIT_ENTRIES = 100
const MAX_IMPORT_BACKLOG_ENTRIES = 50

const initialState: CrmState = {
    rejectionReasons: null,
    mergeAuditLog: [],
    importBacklog: [],
}

const crmSlice = createSlice({
    name: 'crm',
    initialState,
    reducers: {
        setRejectionReasonsCatalog(
            state,
            action: PayloadAction<StoredRejectionReason[]>
        ) {
            state.rejectionReasons = action.payload
        },
        resetRejectionReasonsCatalog(state) {
            state.rejectionReasons = null
        },
        appendMergeAuditLogEntry(
            state,
            action: PayloadAction<Omit<MergeAuditLogEntry, 'id' | 'createdAt'> & Partial<Pick<MergeAuditLogEntry, 'id' | 'createdAt'>>>
        ) {
            const nextEntry: MergeAuditLogEntry = {
                id: action.payload.id ?? `merge_${Date.now()}`,
                createdAt: action.payload.createdAt ?? new Date().toISOString(),
                mainCompanyId: action.payload.mainCompanyId,
                mainCompanyName: action.payload.mainCompanyName,
                mergedCompanyIds: action.payload.mergedCompanyIds,
                mergedCompanyNames: action.payload.mergedCompanyNames,
            }
            state.mergeAuditLog = [nextEntry, ...state.mergeAuditLog].slice(
                0,
                MAX_MERGE_AUDIT_ENTRIES
            )
        },
        setMergeAuditLog(state, action: PayloadAction<MergeAuditLogEntry[]>) {
            state.mergeAuditLog = Array.isArray(action.payload)
                ? action.payload.slice(0, MAX_MERGE_AUDIT_ENTRIES)
                : []
        },
        appendImportBacklogEntry(
            state,
            action: PayloadAction<Omit<ImportBacklogEntry, 'id' | 'createdAt'>>
        ) {
            const nextEntry: ImportBacklogEntry = {
                ...action.payload,
                id: `import_${Date.now()}`,
                createdAt: new Date().toISOString(),
            }
            state.importBacklog = [nextEntry, ...state.importBacklog].slice(
                0,
                MAX_IMPORT_BACKLOG_ENTRIES
            )
        },
        clearImportBacklog(state) {
            state.importBacklog = []
        },
        resetState: () => initialState,
    },
})

export const {
    setRejectionReasonsCatalog,
    resetRejectionReasonsCatalog,
    appendMergeAuditLogEntry,
    setMergeAuditLog,
    appendImportBacklogEntry,
    clearImportBacklog,
    resetState,
} = crmSlice.actions

export default crmSlice.reducer
